<template>
	<div
		v-if="active"
		class="ao-floating-action-wrapper"
		:style="[`padding-bottom: ${heightOfPanel + bottomIndent}px`]"
	>
		<slot name="default" />
		<div
			class="ao-floating-action-panel"
			:style="[`--ao-floating-action-panel-bottom-indent: ${bottomIndent}px`]"
		>
			<ActionButton
				v-if="prependAction"
				class="ao-floating-action-panel__single-button"
				:class="{
					'ao-floating-action-panel__single-button_without-label': !prependAction.label,
					'ao-floating-action-panel__single-button_horizontal': !stacked,
				}"
				:stacked="stacked"
				:label="actions.length ? null : prependAction.label"
				:iconName="prependAction.iconName"
				:badgeStatusColor="prependAction.badgeStatusColor"
				:badge="getBadgeCount(prependAction.badge, 'prepend')"
				:color="prependAction.color"
				:rounded="actions.length ? false : true"
				@click="prependAction.onClick"
			/>

			<div
				v-if="actions.length"
				class="ao-floating-action-panel__group"
			>
				<ActionButton
					v-for="action in actions"
					:key="action.type"
					class="ao-floating-action-panel__group-button"
					:text="action.label"
					:stacked="stacked"
					:label="action.label"
					:iconName="action.iconName"
					:badgeStatusColor="action.badgeStatusColor"
					:badge="getBadgeCount(action.badge, action.type)"
					:badgeFormatterCallback="badgeFormatterCallback"
					:color="action.color"
					@click="action.onClick"
				/>
			</div>

			<ActionButton
				v-if="appendAction"
				class="ao-floating-action-panel__single-button"
				:class="{
					'ao-floating-action-panel__single-button_without-label': !appendAction.label,
					'ao-floating-action-panel__single-button_horizontal': !stacked,
				}"
				:stacked="stacked"
				:label="appendAction.label"
				:iconName="appendAction.iconName"
				:badgeStatusColor="appendAction.badgeStatusColor"
				:badge="getBadgeCount(appendAction.badge, 'append')"
				:badgeFormatterCallback="badgeFormatterCallback"
				:color="appendAction.color"
				:rounded="actions.length ? false : true"
				@click="appendAction.onClick"
			/>
		</div>
	</div>
</template>

<script setup>
import ActionButton from './ActionButton.vue'
import { actionValidator } from './actionValidator'

/**
 * @typedef {object} ActionObject
 * @property {string} type - Уникальный идентификатор действия. Используется в качестве `key` в цикле v-for. **Обязательное поле.**
 * @property {string} iconName - Имя иконки для отображения. **Обязательное поле.**
 * @property {string} [label] - Текстовая метка для кнопки. Если не указана, кнопка будет круглой и содержать только иконку. (Необязательное)
 * @property {string|number} [badge] - Значение для отображения в "бейдж" (значок уведомления) на кнопке. (Необязательное)
 * @property {'success'|'warning'|'danger'|'info'} [badgeStatusColor] - Цвет статуса для бейджа. (Необязательное)
 * @property {function(): void} [onClick] - Функция обратного вызова, которая будет вызвана при клике на кнопку. **Внимание: не проверяется валидатором, но используется в шаблоне!**
 * @property {string} [color] - Цвет кнопки. **Внимание: не проверяется валидатором, но используется в шаблоне!**
 */

const props = defineProps({
	active: {
		type: Boolean,
		default: true,
	},

	/**
	 * Массив объектов действий для отображения в центральной сгруппированной панели.
	 * @type {Array<ActionObject>}
	 */
	actions: {
		type: Array,
		validator: (value) => Array.isArray(value) && value.every(actionValidator),
	},

	/**
	 * Отдельное действие, которое отображается слева (или первым) от основной группы.
	 * @type {ActionObject}
	 */
	prependAction: {
		type: Object,
		validator: actionValidator,
	},

	/**
	 * Отдельное действие, которое отображается справа (или последним) от основной группы.
	 * @type {ActionObject}
	 */
	appendAction: {
		type: Object,
		validator: actionValidator,
	},

	/**
	 * Функция для кастомного форматирования значения в бейдже.
	 * Принимает оригинальное значение (string | number) и должна вернуть строку.
	 * @type {?function(string|number): string}
	 */
	badgeFormatterCallback: {
		type: Function,
		required: false,
	},

	stacked: {
		type: Boolean,
	},

	bottomIndent: {
		type: Number,
		default: 12,
	},
})

defineEmits({
	'update:active': null,
})

const heightOfPanel = 62

const getBadgeCount = (badgeCount, type) => {
	if (props.badgeFormatterCallback) {
		return props.badgeFormatterCallback(badgeCount, type)
	} else {
		if (typeof badgeCount === 'string') {
			return badgeCount
		}
		if (Number(badgeCount) > 10) return '9+'
		return badgeCount
	}
}
</script>

<style lang="scss">
.ao-floating-action-wrapper {
	display: flex;
	flex-direction: column;
	height: 100%;
}

.ao-floating-action-panel {
	--single-button-padding-side-padding: 0px;
	height: 63px;
	margin-top: auto;

	position: sticky;
	bottom: max(
		var(--ao-floating-action-panel-bottom-indent),
		calc(var(--ao-floating-action-panel-bottom-indent) + max(var(--v-layout-bottom, 0px), 0px))
	);
	z-index: 1000;

	display: flex;
	align-items: center;
	justify-content: center;
	gap: 16px;

	& &__single-button {
		&_without-label {
			min-width: 48px;
			width: 48px;
			border-radius: 50%;
			padding: 0;
			--single-button-padding-side-padding: 0px;

			.v-btn__content {
				gap: 0;
			}
		}

		&_horizontal {
			border-radius: 90px;
			--single-button-padding-side-padding: 0px;
			padding: 12px 20px;
		}

		max-height: 48px;
		height: 48px;
		border-radius: 32px !important;
		padding: 4px 12px;
		--single-button-padding-side-padding: 12px;
		background-color: white;
		display: flex;
		justify-content: center;
		align-items: center;
		box-shadow: 0px 4px 18.1px rgba(0, 0, 0, 0.25);

		.v-badge__badge {
			left: calc(100% - (20px + var(--single-button-padding-side-padding))) !important;
		}
	}

	&__group {
		max-height: 63px;
		background: white;
		border-radius: 32px;
		display: flex;
		justify-content: space-around;
		align-items: center;
		gap: 6px;
		padding: 8px 10px;
		box-shadow: 0px 4px 18.1px rgba(0, 0, 0, 0.25);
	}

	& &__group-button {
		max-height: 48px;
		min-width: 60px;
		flex: 1;
		background: transparent;
		border: none;
		outline: none;
		text-align: center;
		position: relative;
		padding: 0;
		box-shadow: none;

		&:hover {
			box-shadow: none;
		}

		.v-badge__badge {
			left: calc(100% - 20px) !important;
		}
	}

	.v-btn__overlay {
		background: none;
	}
}
</style>
